import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Key } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import '../styles/Page.css';
import '../styles/Forms.css';
import '../styles/AdminPortalAdditions.css';

const hostURL = config.serverUrl;

// Password validation function
const validatePassword = (password) => {
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters long' };
  }
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  if (!hasUppercase) {
    return { valid: false, error: 'Password must include at least one uppercase letter' };
  }
  if (!hasLowercase) {
    return { valid: false, error: 'Password must include at least one lowercase letter' };
  }
  if (!hasNumber && !hasSymbol) {
    return { valid: false, error: 'Password must include at least one number or symbol' };
  }
  return { valid: true };
};

function EmployeePortal() {
  const { currentUser, authenticate, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Change password popup state
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    authenticate(email, password);
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      return;
    }

    try {
      await axios.put(
        `${hostURL}/api/auth/change-password`,
        { currentPassword, newPassword, confirmPassword },
        { withCredentials: true }
      );

      setSuccess('Password changed successfully!');
      setShowChangePasswordPopup(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  // Clear success message when the logged-in user changes (prevents bleed between sessions)
  useEffect(() => {
    setSuccess('');
  }, [currentUser]);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  const closeChangePasswordPopup = () => {
    setShowChangePasswordPopup(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  // Check if user is admin or owner
  const isAdminOrOwner = currentUser && (currentUser.access === 'admin' || currentUser.access === 'owner');
  const isOwner = currentUser && currentUser.access === 'owner';

  if (!currentUser) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Employee Portal</h1>
          <p>Access your employee resources and information</p>
        </div>

        <div className="page-content">
          <div className="login-container">
            <form className="login-form" onSubmit={handleLogin}>
              <h2>Login</h2>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
              <button type="submit" className="submit-button">Sign In</button>
              <div className="form-links">
                <a href="#forgot">Forgot Password?</a>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Employee Portal</h1>
        <p>Welcome back, {currentUser.email}!</p>
        {isAdminOrOwner && (
          <p className="user-badge">{currentUser.access.toUpperCase()}</p>
        )}
      </div>

      <div className="page-content">
        {success && <div className="success-message">{success}</div>}

        {isOwner && (
          <section className="portal-section">
            <h2>Quick Links</h2>
            <div className="portal-grid">
              <div
                className="portal-card clickable-card"
                onClick={() => navigate('/configure-admins')}
              >
                <div className="portal-icon"><Settings size={48} color="#4dd0e1" /></div>
                <h3>Configure Admins</h3>
                <p>Manage administrative users</p>
              </div>
            </div>
          </section>
        )}

        {isAdminOrOwner && (
          <section className="portal-section">
            <h2>Account</h2>
            <div className="portal-grid">
              <div
                className="portal-card clickable-card"
                onClick={() => { setError(''); setShowChangePasswordPopup(true); }}
              >
                <div className="portal-icon"><Key size={48} color="#4dd0e1" /></div>
                <h3>Change Password</h3>
                <p>Update your account password</p>
              </div>
            </div>
          </section>
        )}

        <button
          className="logout-button"
          onClick={() => logout()}
        >
          Logout
        </button>
      </div>

      {/* Change Password Popup */}
      {showChangePasswordPopup && (
        <div className="popup-overlay" onClick={closeChangePasswordPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h2>Change Password</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleChangePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <small style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Min 8 characters, must include: uppercase, lowercase, and (numbers or symbols)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="popup-buttons">
                <button type="button" onClick={closeChangePasswordPopup} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="confirm-button">
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeePortal;
